import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CalendarDays, Clock, CheckCircle2, PlusCircle, MapPin } from "lucide-react";
import { toast } from "sonner";

function getStatusLabel(status) {
  const labels = {
    nouvelle: "Nouvelle",
    a_analyser: "À analyser",
    acceptee: "Acceptée",
    soignant_a_attribuer: "Soignant à attribuer",
    soignant_attribue: "Soignant attribué",
    effectuee: "Effectuée",
    cloturee: "Clôturée",
    refusee: "Refusée",
  };
  return labels[status] || status || "—";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-BE");
}

export default function TitulaireDashboard() {
  const { axiosInstance, user } = useAuth();
  const [remplacements, setRemplacements] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRemplacements = async () => {
    try {
      const res = await axiosInstance.get("/remplacements-domicile/me");
      setRemplacements(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.detail ||
        "Impossible de charger vos demandes de remplacement."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRemplacements();
  }, []);

  const stats = useMemo(() => {
    return {
      total: remplacements.length,
      pending: remplacements.filter((r) =>
        ["nouvelle", "a_analyser", "acceptee", "soignant_a_attribuer"].includes(r.statut)
      ).length,
      assigned: remplacements.filter((r) => r.statut === "soignant_attribue").length,
      completed: remplacements.filter((r) =>
        ["effectuee", "cloturee"].includes(r.statut)
      ).length,
    };
  }, [remplacements]);

  const upcoming = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return remplacements
      .filter((r) => {
        if (!r.date) return false;
        const d = new Date(`${r.date}T12:00:00`);
        return d >= today && r.statut !== "refusee";
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);
  }, [remplacements]);

  return (
    <Layout title={`Bienvenue, ${user?.first_name || user?.name || "Titulaire"} !`}>
      <div className="space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-[#315f63]">
              Remplacements de soins à domicile
            </h2>
            <p className="mt-1 text-sm text-[#4f7d80]">
              Introduisez vos demandes et suivez leur prise en charge par Sana Care.
            </p>
          </div>

          <Link to="/remplacements-domicile/nouveau">
            <Button className="bg-[#6faeb2] hover:bg-[#5b979b] text-white">
              <PlusCircle size={18} className="mr-2" />
              Nouvelle demande
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card className="border-[#d4ebeb] bg-white">
            <CardContent className="p-5">
              <p className="text-sm text-gray-500">Demandes totales</p>
              <p className="mt-2 text-3xl font-bold text-[#315f63]">{stats.total}</p>
            </CardContent>
          </Card>

          <Card className="border-[#d4ebeb] bg-white">
            <CardContent className="p-5">
              <p className="text-sm text-gray-500">En attente</p>
              <p className="mt-2 text-3xl font-bold text-amber-700">{stats.pending}</p>
            </CardContent>
          </Card>

          <Card className="border-[#d4ebeb] bg-white">
            <CardContent className="p-5">
              <p className="text-sm text-gray-500">Soignant attribué</p>
              <p className="mt-2 text-3xl font-bold text-blue-700">{stats.assigned}</p>
            </CardContent>
          </Card>

          <Card className="border-[#d4ebeb] bg-white">
            <CardContent className="p-5">
              <p className="text-sm text-gray-500">Effectuées / clôturées</p>
              <p className="mt-2 text-3xl font-bold text-green-700">{stats.completed}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-[#d4ebeb] bg-[#f4fafa]">
          <CardHeader>
            <CardTitle className="text-[#315f63]">Prochaines demandes</CardTitle>
          </CardHeader>

          <CardContent>
            {loading ? (
              <p className="text-[#4f7d80]">Chargement…</p>
            ) : upcoming.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#b8dede] bg-white p-8 text-center">
                <CalendarDays className="mx-auto mb-3 text-[#6faeb2]" size={32} />
                <p className="font-semibold text-[#315f63]">
                  Aucune demande prévue
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.map((item) => (
                  <div
                    key={item._id}
                    className="rounded-xl border border-[#d4ebeb] bg-white p-4"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 font-semibold text-[#315f63]">
                          <CalendarDays size={16} />
                          {formatDate(item.date)}
                        </div>

                        <div className="flex items-center gap-2 text-sm text-[#4f7d80]">
                          <MapPin size={15} />
                          {item.zone || "Zone non précisée"}
                        </div>

                        <div className="text-sm text-gray-600">
                          {item.nombre_patients || 0} patient(s)
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="inline-flex rounded-full bg-[#e6f3f3] px-3 py-1 text-xs font-semibold text-[#315f63]">
                          {getStatusLabel(item.statut)}
                        </span>

                        {item.soignant_name && (
                          <p className="mt-2 text-sm text-gray-600">
                            Soignant : <strong>{item.soignant_name}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5">
              <Link to="/remplacements-domicile">
                <Button variant="outline" className="border-[#b8dede] text-[#315f63]">
                  Voir toutes mes demandes
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
